<script setup>
import UmisisModal from '../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { computed, ref } from 'vue'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../resources/js/components/ui/field/index.js'
import InputText from '../../../../../../../resources/js/components/umisis/form/inputs/InputText.vue'
import InputCheckbox from '../../../../../../../resources/js/components/umisis/form/inputs/InputCheckbox.vue'
import { useBusinessSystemStore } from '../../stores/businessSystems.js'
import { useBusinessSystemCategoryStore } from '../../stores/businessSystemCategories.js'
import { useApiAction } from '../../../../../../../resources/js/composables/dispatchers/useApiAction.js'
import { toast } from 'vue-sonner'

const { componentRef: dialog, openModal, closeModal } = useModalControl()
const emit = defineEmits(['created'])
const businessSystemStore = useBusinessSystemStore()
const businessSystemCategoryStore = useBusinessSystemCategoryStore()

const systemName = ref('')
const systemObjective = ref('')
const businessSystemCategoryId = ref(null)
const businessSystemFrequency = ref('on_demand')
const businessSystemIsPublic = ref(false)

const frequencyOptions = computed(() => businessSystemStore.getFrequencyOptions)
const categoryOptions = computed(() => businessSystemCategoryStore.items)

const fieldControlBaseClass = 'flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
const fieldControlClass = (fieldName) => ([
  fieldControlBaseClass,
  validationResult(fieldName) === false ? 'border-destructive focus-visible:ring-destructive bg-red-50' : '',
  validationResult(fieldName) === true ? 'border-success focus-visible:ring-success bg-green-50' : '',
])

const setDefaults = () => {
  systemName.value = ''
  systemObjective.value = ''
  businessSystemCategoryId.value = null
  businessSystemFrequency.value = frequencyOptions.value[0]?.value ?? 'on_demand'
  businessSystemIsPublic.value = false
}

const onShow = async () => {
  setDefaults()
  await businessSystemCategoryStore.fetchItems({
    not_paginated: true,
    clear: true,
  })

  if (categoryOptions.value.length > 0) {
    businessSystemCategoryId.value = categoryOptions.value[0].id
  }
}

const { execute, loading, getError, validationResult } = useApiAction((payload) => {
  return businessSystemStore.createBusinessSystem(payload)
}, {
  onSuccess: (businessSystem) => {
    toast.success('Business system created successfully.')
    emit('created', businessSystem)
    closeModal()
  },
})

defineExpose({
  openModal,
  closeModal
})

const handleOk = async (bvEvent) => {
  if (bvEvent) {
    bvEvent.preventDefault()
  }

  await execute({
    name: systemName.value,
    objective: systemObjective.value || null,
    business_system_category_id: businessSystemCategoryId.value !== null ? Number(businessSystemCategoryId.value) : null,
    frequency: businessSystemFrequency.value,
    public: businessSystemIsPublic.value,
  })
}
</script>

<template>
  <UmisisModal
    ref="dialog"
    title="Create Business System"
    description="Create a new system and assign its operational defaults."
    ok-title="Create"
    cancel-title="Cancel"
    :ok-disabled="loading || businessSystemCategoryStore.isLoading"
    @ok="handleOk"
    @show="onShow"
  >
    <div class="flex flex-col gap-4">
      <InputText
        v-model="systemName"
        label="Name"
        placeholder="Customer Onboarding"
        :validation="validationResult('name')"
        :error-message="getError('name')"
      />

      <Field>
        <FieldLabel for="business-system-objective">
          Objective
        </FieldLabel>
        <textarea
          id="business-system-objective"
          v-model="systemObjective"
          rows="2"
          placeholder="Describe the main objective of this system"
          :class="fieldControlClass('objective')"
        />
        <FieldDescription v-if="getError('objective')" class="text-destructive font-medium">
          {{ getError('objective') }}
        </FieldDescription>
      </Field>

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field>
          <FieldLabel for="business-system-category">
            Category
          </FieldLabel>
          <select
            id="business-system-category"
            v-model="businessSystemCategoryId"
            :class="fieldControlClass('business_system_category_id')"
          >
            <option
              v-for="category in categoryOptions"
              :key="category.id"
              :value="category.id"
            >
              {{ category.name }}
            </option>
          </select>
          <FieldDescription v-if="getError('business_system_category_id')" class="text-destructive font-medium">
            {{ getError('business_system_category_id') }}
          </FieldDescription>
        </Field>

        <Field>
          <FieldLabel for="business-system-frequency">
            Review Frequency
          </FieldLabel>
          <select
            id="business-system-frequency"
            v-model="businessSystemFrequency"
            :class="fieldControlClass('frequency')"
          >
            <option
              v-for="frequencyOption in frequencyOptions"
              :key="frequencyOption.value"
              :value="frequencyOption.value"
            >
              {{ frequencyOption.label }}
            </option>
          </select>
          <FieldDescription v-if="getError('frequency')" class="text-destructive font-medium">
            {{ getError('frequency') }}
          </FieldDescription>
        </Field>
      </div>

      <InputCheckbox
        v-model="businessSystemIsPublic"
        label="Publicly visible"
        :validation="validationResult('public')"
        :error-message="getError('public')"
      />
    </div>
  </UmisisModal>
</template>

<style scoped>

</style>
